import React, { useRef, useState } from "react";
import { Button, Input, Tooltip } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import { SendOutlined, PaperClipOutlined } from "@ant-design/icons";
import { askAiTokens as t } from "./tokens";
import { Attachment } from "./types";
import { ACCEPTED_FILE_TYPES, MAX_FILE_SIZE_MB } from "./fileUtils";
import AttachmentChips from "./AttachmentChips";
import { useIsMobile } from "./useMediaQuery";

interface Props {
  value: string;
  onChange: (next: string) => void;
  onSend: () => void;
  onStop: () => void;
  onPickFiles: (files: FileList) => void;
  onRemoveAttachment: (id: string) => void;
  attachments: Attachment[];
  isStreaming: boolean;
  disabled?: boolean;
  placeholder?: string;
  // When true the composer sits in a centered ~760px column so it lines up
  // with the message list. Otherwise it stretches left-aligned to flank the
  // hero on the empty state.
  centered?: boolean;
}

const ChatInput: React.FC<Props> = ({
  value,
  onChange,
  onSend,
  onStop,
  onPickFiles,
  onRemoveAttachment,
  attachments,
  isStreaming,
  disabled,
  placeholder,
  centered = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  const [focused, setFocused] = useState(false);
  const canSend =
    (value.trim().length > 0 || attachments.length > 0) && !isStreaming;

  return (
    <div
      style={{
        width: "100%",
        // Empty state: stretch from the hero edge on the left to the marquee
        // edge on the right.
        // Chat view: live in a centered max-width column so the composer
        // lines up with the assistant/user messages above.
        padding: isMobile
          ? `${t.space.s2}px ${t.space.s3}px ${t.space.s4}px`
          : centered
          ? `${t.space.s3}px ${t.space.s6}px ${t.space.s5}px`
          : `${t.space.s3}px ${t.space.s8}px ${t.space.s5}px`,
        fontFamily: t.font.sans,
        borderTop: centered ? `1px solid ${t.color.border}` : undefined,
        background: centered
          ? "linear-gradient(180deg, rgba(255,255,255,0) 0%, #ffffff 24%)"
          : undefined,
      }}
    >
      <div
        style={
          centered
            ? {
                maxWidth: t.layout.contentMaxWidth,
                margin: "0 auto",
              }
            : undefined
        }
      >
        <AttachmentChips
          attachments={attachments}
          onRemove={onRemoveAttachment}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_TYPES}
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files) onPickFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div
          className={`askai-composer ${focused ? "askai-composer-focused" : ""}`}
          style={{
            position: "relative",
            padding: 1.5,
            borderRadius: t.radius.lg + 1,
            background: focused
              ? "conic-gradient(from var(--angle), #2563eb, #7c3aed, #ec4899, #22d3ee, #2563eb)"
              : t.color.border,
            transition: "background 0.25s ease",
          }}
        >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: t.space.s2,
            padding: `${t.space.s3}px ${t.space.s4}px`,
            background: t.color.surface,
            borderRadius: t.radius.lg,
            boxShadow: focused
              ? "0 4px 14px -10px rgba(15, 23, 42, 0.18)"
              : "0 2px 6px -4px rgba(15, 23, 42, 0.08)",
            transition: "box-shadow 0.2s ease",
          }}
        >
          <Input.TextArea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                if (canSend) onSend();
              }
            }}
            placeholder={placeholder || "Ask anything about this tender…"}
            autoSize={{ minRows: 2, maxRows: 8 }}
            disabled={disabled}
            bordered={false}
            style={{
              padding: "6px 0",
              resize: "none",
              boxShadow: "none",
              background: "transparent",
              fontSize: 16,
              lineHeight: 1.55,
              color: t.color.text,
              fontFamily: t.font.sans,
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: t.space.s1,
                alignItems: "center",
              }}
            >
              <Tooltip title={`Attach files (max ${MAX_FILE_SIZE_MB} MB each)`}>
                <Button
                  type="text"
                  shape="circle"
                  icon={<PaperClipOutlined style={{ fontSize: 16 }} />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                  style={{ color: t.color.textMuted }}
                />
              </Tooltip>
              <AnimatePresence>
                {focused && (
                  <motion.span
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -4 }}
                    transition={{ duration: 0.15 }}
                    style={{
                      fontSize: t.font.size.xs,
                      color: t.color.textSubtle,
                      paddingLeft: 2,
                    }}
                  >
                    <kbd className="askai-kbd">↵</kbd> to send ·{" "}
                    <kbd className="askai-kbd">Shift</kbd>+
                    <kbd className="askai-kbd">↵</kbd> newline
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            <Tooltip
              title={
                isStreaming
                  ? "Generating…"
                  : canSend
                  ? "Send"
                  : "Type a message first"
              }
            >
              <motion.button
                type="button"
                onClick={onSend}
                disabled={!canSend}
                whileHover={canSend ? { scale: 1.06 } : undefined}
                whileTap={canSend ? { scale: 0.92 } : undefined}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  cursor: canSend ? "pointer" : "not-allowed",
                  background: canSend
                    ? "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)"
                    : t.color.surfaceMuted,
                  color: canSend ? "#fff" : t.color.textSubtle,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s ease",
                }}
              >
                <SendOutlined style={{ fontSize: 14 }} />
              </motion.button>
            </Tooltip>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ChatInput;
